import { useCallback } from "react";
import { useAuthStore } from "@/stores/useAuthStore";
import { useMockAuth } from "@/config/privy";
import { RETURN_URL_KEY } from "@/lib/returnUrl";

/**
 * The one key for "where to send the user once they are authenticated". Every
 * writer goes through setReturnUrl so the URL shape (path + search + hash) is
 * decided in one place — the hash matters: on /r it IS the outreach token.
 * Defined in lib/returnUrl so a reader need not import this hook.
 */
export { RETURN_URL_KEY };

/**
 * Frictionless onboarding: when a user clicks "I'm interested" on a quest
 * without being logged in, we store the quest context so that after Privy
 * login + profile sync we can auto-onboard them, add the quest's required
 * skills to their profile, and express interest — all without the 8-step
 * onboarding flow.
 */
export const FRICTIONLESS_ONBOARDING_KEY = "wexare_frictionless_onboarding";

export interface FrictionlessOnboardingContext {
  questId: string;
  skills: string[];
  /**
   * The referral link this visitor arrived through, if any. It rides here
   * rather than in its own storage slot because this context already IS the
   * across-the-Privy-redirect transport and is already keyed on questId — so
   * the code inherits that scoping (a code stored for quest A can never
   * attribute an application to quest B) and the clear-on-success for free.
   */
  referralCode?: string;
}

export function setFrictionlessOnboarding(context: FrictionlessOnboardingContext): void {
  sessionStorage.setItem(FRICTIONLESS_ONBOARDING_KEY, JSON.stringify(context));
}

export function getFrictionlessOnboarding(): FrictionlessOnboardingContext | null {
  const raw = sessionStorage.getItem(FRICTIONLESS_ONBOARDING_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as FrictionlessOnboardingContext;
  } catch {
    return null;
  }
}

export function clearFrictionlessOnboarding(): void {
  sessionStorage.removeItem(FRICTIONLESS_ONBOARDING_KEY);
}

/** Current location including search and hash, or the given URL verbatim. */
export function setReturnUrl(url?: string): void {
  sessionStorage.setItem(
    RETURN_URL_KEY,
    url ?? `${window.location.pathname}${window.location.search}${window.location.hash}`,
  );
}

export function clearReturnUrl(): void {
  sessionStorage.removeItem(RETURN_URL_KEY);
}

/**
 * Provides a mock-mode login prompt that keeps the user on the current page.
 * Sets mock session in place — page re-renders with isAuthenticated === true.
 *
 * For Privy mode, use the PrivyAuthCta component instead (it needs PrivyProvider).
 *
 * Stores the return URL (the current one, or the one passed) in sessionStorage
 * so that if the user ends up on the home page after auth, they can be
 * redirected back.
 */
export function useAuthPrompt() {
  const loginAsMock = useAuthStore((s) => s.loginAsMock);

  const promptLogin = useCallback(
    (returnUrl?: string) => {
      setReturnUrl(returnUrl);
      if (useMockAuth) {
        loginAsMock();
      }
    },
    [loginAsMock],
  );

  return { promptLogin, isMockAuth: useMockAuth };
}

/**
 * Check and consume a stored return URL after authentication.
 */
export function consumeReturnUrl(): string | null {
  const url = sessionStorage.getItem(RETURN_URL_KEY);
  if (url) {
    clearReturnUrl();
  }
  return url;
}
